import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslatePipe } from '@ngx-translate/core';
import { CrmWaterInformationService } from '@services/crm-water-information.service';
import { BoolComponent } from '@shared/components/bool/bool.component';
import { catchError, of } from 'rxjs';

@Component({
  selector: 'app-water-information-card',
  templateUrl: './water-information-card.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatCardModule, MatIconModule, MatProgressSpinnerModule, TranslatePipe, BoolComponent],
})
export class WaterInformationCardComponent {
  readonly waterId = input.required<number>();

  private readonly informationService = inject(CrmWaterInformationService);

  readonly informationResource = rxResource({
    params: () => ({ waterId: this.waterId() }),
    stream: ({ params }) => this.informationService.get(params.waterId).pipe(catchError(() => of(null))),
  });

  readonly information = computed(() => this.informationResource.value() ?? null);
  readonly isLoading = this.informationResource.isLoading;

  readonly hasAnyContent = computed(() => {
    const i = this.information();
    if (!i) return false;
    return (
      !!i.shortDescriptionNl ||
      !!i.placeNl ||
      i.expectations.length > 0 ||
      i.photos.length > 0 ||
      !!i.appHeaderPhotoName ||
      !!i.mapImageName ||
      !!i.locationImageName ||
      !!i.ambianceVideoImageName ||
      i.hotspots.length > 0 ||
      !!i.hotspotIntroductionNl ||
      i.contentBlocks.length > 0 ||
      i.priceLabels.length > 0 ||
      !!i.priceIntroductionNl ||
      !!i.plainPriceSummaryNl ||
      !!i.plainPriceServicesTextNl ||
      !!i.locationCoordinates ||
      !!i.locationAddressNl ||
      !!i.metaDescriptionNl
    );
  });

  readonly mediaItems = computed(() => {
    const i = this.information();
    if (!i) return [];
    return [
      { label: 'waters.information.section.app-header', set: !!i.appHeaderPhotoName },
      { label: 'waters.information.section.map-image', set: !!i.mapImageName },
      { label: 'waters.information.section.location-image', set: !!i.locationImageName },
      { label: 'waters.information.ambiance-poster', set: !!i.ambianceVideoImageName },
    ];
  });

  readonly pricingItems = computed(() => {
    const i = this.information();
    if (!i) return [];
    return [
      { label: 'waters.information.section.plain-price-summary', set: !!i.plainPriceSummaryNl },
      { label: 'waters.information.section.plain-price-services-text', set: !!i.plainPriceServicesTextNl },
    ];
  });

  readonly contentBlockStats = computed(() => {
    const i = this.information();
    if (!i) return [];
    const counts: Record<string, number> = {};
    for (const b of i.contentBlocks) {
      const t = b.type ?? 'text';
      counts[t] = (counts[t] ?? 0) + 1;
    }
    const iconMap: Record<string, string> = { video: 'videocam', iframe: 'code', text: 'text_fields' };
    return Object.entries(counts).map(([type, count]) => ({
      labelKey: `waters.information.content-block.type.${type}`,
      icon: iconMap[type] ?? 'article',
      count,
    }));
  });
}
